const TRANSITION_DURATION_MS = 300;

class Biography {
  constructor(container, button, collapsibleContent) {
    this.container = container;
    this.button = button;
    this.buttonText = this.button.querySelector('.biography-toggle-button-text');
    this.collapsibleContent = collapsibleContent;
    this.isAnimating = false;
    this.heightCache = null;

    this.init();
  }

  init() {
    this.collapsibleContent.style.blockSize = '0px';

    this.handleMouseEnter = () => this.preCalculateHeight();
    this.handleFocus = () => this.preCalculateHeight();
    this.handleClick = () => this.handleClickEvent();

    this.button.addEventListener('mouseenter', this.handleMouseEnter);
    this.button.addEventListener('focus', this.handleFocus);
    this.button.addEventListener('click', this.handleClick);

    let resizeTimeout;
    this.handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.heightCache = null;
      }, 250);
    };
    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    this.button.removeEventListener('mouseenter', this.handleMouseEnter);
    this.button.removeEventListener('focus', this.handleFocus);
    this.button.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.handleResize);

    this.collapsibleContent.style.blockSize = '';
    this.container.classList.remove('is-expanded');
    this.button.setAttribute('aria-expanded', 'false');
    this.buttonText.textContent = this.button.dataset.expandText;
  }

  preCalculateHeight() {
    if (this.container.classList.contains('is-expanded') || this.heightCache !== null) {
      return;
    }

    const originalBlockSize = this.collapsibleContent.style.blockSize;
    const originalVisibility = this.container.style.visibility;

    this.container.style.visibility = 'hidden';
    this.collapsibleContent.style.blockSize = '';
    this.container.classList.add('is-expanded');

    // Forced reflow, chrome was not updating values on time
    /* eslint-disable-next-line no-unused-expressions */
    this.collapsibleContent.offsetHeight;
    const naturalHeight = this.collapsibleContent.scrollHeight;

    this.container.classList.remove('is-expanded');
    this.collapsibleContent.style.blockSize = originalBlockSize;
    this.container.style.visibility = originalVisibility;

    this.heightCache = naturalHeight;
  }

  handleClickEvent() {
    if (this.isAnimating) {
      return;
    }

    const isExpanded = this.container.classList.contains('is-expanded');

    if (isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    this.isAnimating = true;
    this.container.classList.add('is-expanded');
    this.button.setAttribute('aria-expanded', 'true');
    this.buttonText.textContent = this.button.dataset.collapseText;

    if (this.heightCache !== null) {
      this.collapsibleContent.style.blockSize = '0px';
      requestAnimationFrame(() => {
        this.collapsibleContent.style.blockSize = this.heightCache + 'px';
        this.finishAnimation(true);
      });
    } else {
      this.collapsibleContent.style.blockSize = 'auto';
      const naturalHeight = this.collapsibleContent.offsetHeight;
      this.collapsibleContent.style.blockSize = '0px';

      requestAnimationFrame(() => {
        this.collapsibleContent.style.blockSize = naturalHeight + 'px';
        this.heightCache = naturalHeight;
        this.finishAnimation(true);
      });
    }
  }

  collapse() {
    this.isAnimating = true;
    const currentHeight = this.collapsibleContent.offsetHeight;
    this.collapsibleContent.style.blockSize = currentHeight + 'px';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.collapsibleContent.style.blockSize = '0px';
        this.finishAnimation(false);
      });
    });
  }

  finishAnimation(isExpanded) {
    setTimeout(() => {
      if (isExpanded) {
        this.collapsibleContent.style.blockSize = 'auto';
      } else {
        this.container.classList.remove('is-expanded');
        this.button.setAttribute('aria-expanded', 'false');
        this.buttonText.textContent = this.button.dataset.expandText;
      }
      this.isAnimating = false;
    }, TRANSITION_DURATION_MS);
  }
}

const MOBILE_BREAKPOINT = 768;
const RESIZE_DEBOUNCE_MS = 250;

const biographyInstances = new Map();

function initializeBiography() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (window.innerWidth >= MOBILE_BREAKPOINT || prefersReducedMotion) {
    return;
  }

  const biographyBlocks = document.querySelectorAll('.wp-block-improntad-model-blocks-doctor-biography');

  biographyBlocks.forEach((block) => {
    if (biographyInstances.has(block)) {
      return;
    }

    const button = block.querySelector('.biography-toggle-button');
    const collapsibleContent = block.querySelector('.doctor-biography-collapsible');

    if (!button || !collapsibleContent) {
      return;
    }

    const instance = new Biography(block, button, collapsibleContent);
    biographyInstances.set(block, instance);
  });
}

function cleanupBiography() {
  biographyInstances.forEach((instance) => {
    instance.destroy();
  });
  biographyInstances.clear();
}

function handleViewportChange() {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const hasInstances = biographyInstances.size > 0;

  if (isMobile && !hasInstances) {
    initializeBiography();
  } else if (!isMobile && hasInstances) {
    cleanupBiography();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initializeBiography();

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      handleViewportChange();
    }, RESIZE_DEBOUNCE_MS);
  });
});
